import React from 'react';
import { CircularProgress, Typography } from '@mui/material';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import errorResponses from '../../../errorResponses';

import { AppContext } from '../../../App';
import { IExtractionBatch, Statuses } from '../../../types/my-types';
import { useQueryClient } from '@tanstack/react-query';
import { useRefreshedTransformed } from '../../hooks/useRefreshTransformed';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItemHiddenTextLabel from '../../common/MenuItemHiddenTextLabel';

import IconButtonWhiteBg from '../../common/IconButtonWhiteBg';
import LoopIcon from '@mui/icons-material/Loop';

interface IProps {
  extractionBatch: IExtractionBatch;
  type: 'menu' | 'icon';
  handleClose?: () => void;
  afterActionSuccess?: () => void;
}

function ExtractionBatchReTransform({
  extractionBatch,
  type,
  handleClose,
  afterActionSuccess,
}: IProps) {
  const { setToast } = React.useContext(AppContext);

  const [open, setOpen] = React.useState(false);

  const runHandleClose = () => {
    if (handleClose) {
      handleClose();
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseClick = () => {
    setOpen(false);
    runHandleClose();
  };

  const cache = useQueryClient();

  const reTransformMutation = useRefreshedTransformed({
    onSuccess: () => {
      cache.invalidateQueries({
        queryKey: ['extractionBatches']
      });
      cache.invalidateQueries({
        queryKey: ['extractionBatch']
      });
      cache.invalidateQueries({
        queryKey: ['allExtractions']
      });
      cache.invalidateQueries({
        queryKey: ['allDocuments']
      });

      setToast({
        variant: 'success',
        open: true,
        message: 'Re-Transform Submitted.',
      });

      if (afterActionSuccess) {
        afterActionSuccess();
      }
    },
    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToReTransform,
      });
    },
  });

  const handleSubmit = async () => {
    await reTransformMutation.mutate(extractionBatch.id);
    setOpen(false);
    runHandleClose();
  };

  const isTransformInProgress = extractionBatch?.data_processing_status === Statuses.InProgress;

  return (
    <>
      {type === 'menu' ? (
        <MenuItem
          id="create-re-extraction-batch"
          data-cy={`btn-re-transform-${extractionBatch.label}`}
          key={'Re-Transform'}
          onClick={handleClickOpen}
          sx={{ fontSize: '0.875rem', padding: '4px 8px' }}
          disabled={!extractionBatch.finished || isTransformInProgress}
        >
          <ListItemIcon>
            <LoopIcon fontSize="small" />
          </ListItemIcon>
          {reTransformMutation.isPending || isTransformInProgress ? (
            <CircularProgress size={20} />
          ) : null}{' '}
          <MenuItemHiddenTextLabel>Re-Transform</MenuItemHiddenTextLabel>
        </MenuItem>
      ) : type === 'icon' ? (
        <IconButtonWhiteBg
          disabled={
            !extractionBatch.finished || reTransformMutation.isPending || isTransformInProgress
          }
          onClick={handleClickOpen}
          id="icon-re-transform"
          cypressId={`icon-re-transform-${extractionBatch.label}`}
          tooltip={
            reTransformMutation.isPending || isTransformInProgress
              ? 'Re-Transforming…'
              : 'Re-Transform'
          }
        >
          {reTransformMutation.isPending || isTransformInProgress ? (
            <CircularProgress size={16} />
          ) : (
            <LoopIcon style={{ fontSize: '15px', color: 'black' }} />
          )}
        </IconButtonWhiteBg>
      ) : null}

      <DialogGlynt open={open} closeModal={handleCloseClick} cypressId="dialog-re-transform">
        <DialogTitleGlynt title="Confirmation" closeModal={handleCloseClick} />
        <DialogContentGlynt>
          <Typography variant="body2" color="textSecondary">
            Under most circumstances, results and corrections are transformed automatically. Are you sure you want to continue?
          </Typography>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleSubmit}
          closeModal={handleCloseClick}
          cancelCypressId="btn-re-transform-cancel"
          submitCypressId="btn-re-transform-yes"
          clearCypressId=""
          submitButtonText="Yes"
        />
      </DialogGlynt>
    </>
  );
}

export default ExtractionBatchReTransform;
